import { Menu, ShieldCheck, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { NotificationBell } from '@/features/notifications/Bell'
import { UserMenu } from './UserMenu'
import { useWsStatus } from '@/hooks/useWebSocket'
import { Badge } from '@/components/ui/Badge'
import { SessionCountdownBadge } from '@/features/auth/SessionCountdownBadge'
import { useBranding } from '@/lib/branding'

interface Props {
  className?: string
  /** Toggle the mobile nav drawer. Hamburger only renders below lg. */
  onToggleNav?: () => void
  navOpen?: boolean
}

export function Header({ className, onToggleNav, navOpen }: Props) {
  const ws = useWsStatus()
  const branding = useBranding()
  return (
    <header
      className={cn(
        'flex items-center justify-between gap-2 border-b border-border bg-card px-3 py-2 sm:px-4',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        {onToggleNav ? (
          <button
            type="button"
            onClick={onToggleNav}
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={!!navOpen}
            className="flex h-9 w-9 items-center justify-center rounded text-fg transition-colors hover:bg-muted lg:hidden"
          >
            {navOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        ) : null}
        {/* Logo first when the bank has one configured; the
            shield icon is the platform's own brand and stays as
            a fallback so the chrome never looks broken. */}
        {branding.logo_url ? (
          <img
            src={branding.logo_url}
            alt={`${branding.display_name} logo`}
            className="h-5 w-5 shrink-0 rounded-sm object-contain"
            // 404s on the configured URL silently swap to the
            // shield icon — the alt text plus the bank's display
            // name still carry meaning.
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        ) : (
          <ShieldCheck className="text-primary shrink-0" size={20} />
        )}
        <span className="font-semibold truncate">
          {branding.display_name || 'Antifraud'}
        </span>
        <Badge
          variant={ws === 'open' ? 'success' : ws === 'connecting' ? 'pending' : 'cancelled'}
          className="hidden sm:inline-flex"
        >
          {ws === 'open' ? 'Live' : ws === 'connecting' ? 'Connecting…' : 'Offline'}
        </Badge>
      </div>
      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        <SessionCountdownBadge />
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  )
}
